import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, Mousewheel } from 'swiper/modules';
import 'swiper/css';

function Carousel() {
    const swiperRef = useRef(null);
    const [activeSlide, setActiveSlide] = useState(0);

    const slides = [
        {
            id: 1,
            imgSrc: '/assets-web/hero_banner_1.webp',
            fallbackSrc: '/assets-web/hero_banner_1.png',
            eyebrow: 'PT Len Telekomunikasi Indonesia',
            title: 'Konektivitas Berkualitas untuk Indonesia',
            description:
                'Mendukung pemerataan konektivitas digital melalui infrastruktur telekomunikasi yang andal dan berkelanjutan.',
            primaryLabel: 'Tentang Perusahaan',
            primaryPath: '/tentang-perusahaan',
            secondaryLabel: 'Hubungi Kami',
            secondaryPath: '/kontak-kami',
        },
        {
            id: 2,
            imgSrc: '/assets-web/hero_banner_2.webp',
            fallbackSrc: '/assets-web/hero_banner_2.png',
            eyebrow: 'Backbone Telekomunikasi Nasional',
            title: 'Palapa Ring Tengah',
            description:
                'Menghubungkan wilayah Kalimantan, Sulawesi, dan Kepulauan Maluku Utara melalui jaringan backbone serat optik.',
            primaryLabel: 'Jelajahi Palapa Ring',
            primaryPath: '/palapa-ring',
            secondaryLabel: 'Lokasi dan Wilayah',
            secondaryPath: '/palapa-ring#lokasi-dan-wilayah',
        },
        {
            id: 3,
            imgSrc: '/assets-web/hero_banner_3.webp',
            fallbackSrc: '/assets-web/hero_banner_3.png',
            eyebrow: 'Informasi Perusahaan',
            title: 'Berita, Artikel, dan Publikasi',
            description:
                'Ikuti perkembangan perusahaan, kegiatan strategis, dan publikasi resmi PT Len Telekomunikasi Indonesia.',
            primaryLabel: 'Lihat Berita',
            primaryPath: '/media/berita',
            secondaryLabel: 'Publikasi',
            secondaryPath: '/media/publikasi',
        },
    ];

    const goToSlide = (index) => {
        swiperRef.current?.slideTo(index);
    };

    return (
        <section
            id="id-carousel"
            className="relative mt-20 h-[calc(100svh-80px)] min-h-[620px] max-h-[940px] overflow-hidden bg-slate-950"
            aria-label="Sorotan utama PT Len Telekomunikasi Indonesia"
        >
            <Swiper
                direction="vertical"
                slidesPerView={1}
                speed={760}
                mousewheel={{
                    forceToAxis: true,
                    releaseOnEdges: true,
                    sensitivity: 0.72,
                    thresholdDelta: 12,
                    thresholdTime: 300,
                }}
                keyboard={{ enabled: true, onlyInViewport: true }}
                touchReleaseOnEdges
                modules={[Mousewheel, Keyboard]}
                onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                }}
                onSlideChange={(swiper) => setActiveSlide(swiper.activeIndex)}
                className="h-full w-full"
            >
                {slides.map((slide) => (
                    <SwiperSlide key={slide.id} className="h-full w-full">
                        <article className="relative h-full w-full overflow-hidden">
                            <picture>
                                <source srcSet={slide.imgSrc} type="image/webp" />
                                <img
                                    src={slide.fallbackSrc}
                                    alt=""
                                    aria-hidden="true"
                                    className="absolute inset-0 h-full w-full object-cover object-center"
                                    loading={slide.id === 1 ? 'eager' : 'lazy'}
                                    decoding="async"
                                />
                            </picture>

                            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(2,6,23,0.93)_0%,rgba(2,6,23,0.74)_42%,rgba(2,6,23,0.34)_72%,rgba(2,6,23,0.48)_100%)]" />
                            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.28)_0%,rgba(2,6,23,0.08)_45%,rgba(2,6,23,0.62)_100%)]" />

                            <div className="relative z-10 mx-auto flex h-full max-w-[1480px] items-center px-5 pb-16 pt-10 sm:px-8 lg:px-12 xl:px-16">
                                <div className="ml-0 max-w-3xl lg:ml-24 xl:ml-28">
                                    <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.08] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-blue-100 backdrop-blur-md sm:text-xs">
                                        <span className="h-1.5 w-1.5 rounded-full bg-blue-400 shadow-[0_0_14px_rgba(96,165,250,0.95)]" />
                                        {slide.eyebrow}
                                    </span>

                                    <h1 className="mt-6 max-w-3xl text-4xl font-black leading-[1.06] tracking-[-0.035em] text-white sm:text-5xl lg:text-6xl xl:text-7xl">
                                        {slide.title}
                                    </h1>
                                    <p className="mt-6 max-w-2xl text-base leading-7 text-slate-200 sm:text-lg sm:leading-8 lg:text-xl">
                                        {slide.description}
                                    </p>

                                    <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                                        <Link
                                            to={slide.primaryPath}
                                            className="inline-flex min-h-[50px] items-center justify-center rounded-full bg-blue-500 px-6 py-3 text-sm font-bold text-white shadow-[0_14px_40px_rgba(37,99,235,0.34)] transition hover:-translate-y-0.5 hover:bg-blue-400 sm:min-w-[190px]"
                                        >
                                            {slide.primaryLabel}
                                            <span className="ml-2" aria-hidden="true">→</span>
                                        </Link>
                                        <Link
                                            to={slide.secondaryPath}
                                            className="inline-flex min-h-[50px] items-center justify-center rounded-full border border-white/35 bg-white/[0.04] px-6 py-3 text-sm font-bold text-white backdrop-blur-sm transition hover:border-white/60 hover:bg-white/[0.1] sm:min-w-[180px]"
                                        >
                                            {slide.secondaryLabel}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </article>
                    </SwiperSlide>
                ))}
            </Swiper>

            <div className="pointer-events-none absolute inset-y-0 left-5 z-20 hidden items-center lg:flex xl:left-8">
                <div className="pointer-events-auto flex flex-col items-center gap-5 rounded-full border border-white/10 bg-slate-950/35 px-3 py-5 backdrop-blur-md">
                    {slides.map((slide, index) => (
                        <button
                            key={slide.id}
                            type="button"
                            onClick={() => goToSlide(index)}
                            className={`group flex h-10 w-10 items-center justify-center rounded-full text-xs font-bold transition ${
                                activeSlide === index
                                    ? 'bg-blue-500 text-white shadow-[0_0_24px_rgba(59,130,246,0.42)]'
                                    : 'text-slate-400 hover:bg-white/10 hover:text-white'
                            }`}
                            aria-label={`Tampilkan sorotan ${index + 1}`}
                            aria-current={activeSlide === index ? 'true' : undefined}
                        >
                            {String(index + 1).padStart(2, '0')}
                        </button>
                    ))}
                </div>
            </div>

            <div className="pointer-events-none absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full border border-white/10 bg-slate-950/35 px-4 py-2 backdrop-blur-md lg:hidden">
                {slides.map((slide, index) => (
                    <button
                        key={slide.id}
                        type="button"
                        onClick={() => goToSlide(index)}
                        className={`pointer-events-auto h-2.5 rounded-full transition-all ${
                            activeSlide === index ? 'w-8 bg-blue-400' : 'w-2.5 bg-white/35'
                        }`}
                        aria-label={`Tampilkan sorotan ${index + 1}`}
                    />
                ))}
            </div>
        </section>
    );
}

export default Carousel;
